<template>
  <b-modal
    id="moveCategoryModal"
    ref="moveCategoryModal"
    title="Mover categoría"
    hide-footer
  >
    <p class="move-intro">
      Mover <strong>{{ category.name }}</strong> a:
    </p>

    <label class="picker-label">Destino</label>
    <div class="tree-picker">
      <button
        type="button"
        class="picker-row picker-root"
        :class="{ 'is-selected': targetId === '' }"
        @click="targetId = ''"
      >
        Categoría principal
        <span
          v-if="currentParentId === ''"
          class="picker-hint"
        >actual</span>
      </button>

      <button
        v-for="option in treeOptions"
        :key="option.id"
        type="button"
        class="picker-row"
        :class="{
          'is-selected': targetId === option.id,
          'is-current': option.id === currentParentId,
        }"
        :style="{ paddingLeft: `${12 + option.depth * 22}px` }"
        @click="targetId = option.id"
      >
        <span class="picker-name">{{ option.name }}</span>
        <span
          v-if="option.id === currentParentId"
          class="picker-hint"
        >actual</span>
      </button>
    </div>

    <p
      v-if="descendantCount"
      class="move-warning"
    >
      Se moverán también sus {{ descendantCount }}
      {{ descendantCount === 1 ? 'subcategoría' : 'subcategorías' }}.
    </p>

    <div class="d-flex justify-content-end">
      <b-button
        variant="outline-secondary"
        class="mr-1"
        @click="hide"
      >
        Cancelar
      </b-button>
      <b-button
        variant="primary"
        :disabled="isSaving || targetId === currentParentId"
        @click="move"
      >
        {{ isSaving ? 'Moviendo…' : 'Mover' }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  props: {
    category: {
      type: Object,
      default: () => ({}),
    },
    categories: {
      type: Array,
      default: () => ([]),
    },
    projectId: {
      type: String,
      default: () => (''),
    },
    currentParentId: {
      type: String,
      default: () => (''),
    },
  },
  data() {
    return {
      targetId: '',
      isSaving: false,
    }
  },
  computed: {
    treeOptions() {
      return this.buildOptions(this.categories, 0)
    },
    descendantCount() {
      const subs = this.category.subcategories || this.category.children || []
      return subs.reduce((total, sub) => total + 1 + this.countDescendants(sub), 0)
    },
  },
  watch: {
    category: {
      immediate: true,
      handler() {
        this.targetId = this.currentParentId || ''
      },
    },
  },
  methods: {
    ...mapActions('groupedExpenses', [
      'moveExpenseTypeCatalog',
    ]),
    countDescendants(node) {
      const subs = node.subcategories || node.children || []
      return subs.reduce((total, sub) => total + 1 + this.countDescendants(sub), 0)
    },
    buildOptions(list, depth) {
      return list.reduce((acc, node) => {
        if (node.id === this.category.id) return acc

        const subs = node.subcategories || node.children || []
        acc.push({ id: node.id, name: node.name, depth })
        return acc.concat(this.buildOptions(subs, depth + 1))
      }, [])
    },
    hide() {
      this.$refs.moveCategoryModal.hide()
    },
    move() {
      this.isSaving = true
      this.moveExpenseTypeCatalog({
        projectId: this.projectId,
        categoryId: this.category.id,
        parentId: this.targetId,
      })
        .then(() => {
          this.$emit('moved', this.targetId)
          this.hide()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Categoría movida',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          const msg = error?.data?.messages?.[0] || 'No se pudo mover la categoría.'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: msg,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.isSaving = false
        })
    },
  },
}
</script>

<style scoped>
.move-intro {
  margin-bottom: 12px;
}

.picker-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
}

.tree-picker {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e6e8ee;
  border-radius: 6px;
  padding: 4px;
  margin-bottom: 12px;
}

body.dark-layout .tree-picker {
  border-color: #3b4253;
}

.picker-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 5px 10px 5px 12px;
  border: 0;
  border-radius: 4px;
  background: none;
  color: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.picker-row:hover {
  background-color: rgba(124, 18, 123, 0.07);
}

.picker-row.is-selected {
  background-color: rgba(124, 18, 123, 0.12);
  font-weight: 600;
}

.picker-root {
  font-weight: 600;
  border-bottom: 1px solid #e6e8ee;
  border-radius: 4px 4px 0 0;
  margin-bottom: 4px;
}

body.dark-layout .picker-root {
  border-bottom-color: #3b4253;
}

/* Guía vertical que hace legible la jerarquía */
.picker-name {
  position: relative;
}

.picker-row:not(.picker-root) .picker-name::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 50%;
  width: 8px;
  height: 1px;
  background-color: #c9ccd6;
}

body.dark-layout .picker-row:not(.picker-root) .picker-name::before {
  background-color: #4a5163;
}

.picker-hint {
  font-size: 10px;
  font-weight: 600;
  color: #8a8f9c;
  border: 1px solid #d8dbe3;
  border-radius: 10px;
  padding: 0 6px;
}

.move-warning {
  font-size: 12px;
  color: #8a8f9c;
}
</style>
